<template>
  <SQDCModalEvent
    v-if="customUserFunctionsReady && resolved.kind === 'event' && resolved.eventType != null"
    :key="`event${modalKey}`"
    :visible="modalVisible"
    :event-type="resolved.eventType"
    :default-event="resolved.defaultEvent"
    :site-id="siteId"
    :board-id="boardId ?? undefined"
    :event-id="resolved.eventId"
    :initial-conversion-target-id="resolved.initialConversionTargetId"
    :workflow-instance-id="activeWorkflowInstanceId"
    :workflow-step-id="activeWorkflowStepIdForChild"
    :workflow-definition-id="resolved.workflowDefinitionId"
    :workflow-definition="resolved.workflowDefinition"
    :workflow-node="activeNode"
    :workflow="resolved.workflow"
    :create-silently="resolved.createSilently"
    :technical-place-default="technicalPlaceDefault"
    :cost-center-default="costCenterDefault"
    :user-option-ids="modalUserOptionIds"
    :user-field-option-ids="userFieldOptionIds"
    :user-field-role-labels="userFieldRoleLabels"
    :responsible-role-label="responsibleRoleLabel"
    workflow-step-host
    @save="handleSave"
    @workflow-next="handleWorkflowNext"
    @removed-from-board="handleRemovedFromBoard"
    @update:visible="handleVisible"
  />
  <SQDCModalAction
    v-else-if="
      customUserFunctionsReady &&
      resolved.kind === 'action' &&
      resolved.actionType != null &&
      resolved.eventId != null
    "
    :key="`action${modalKey}`"
    :visible="modalVisible"
    :action-type="resolved.actionType"
    :default-action="resolved.defaultAction"
    :action-id="resolved.actionId"
    :event-id="resolved.eventId"
    :event-field-id="resolved.eventFieldId"
    :event-info-field-name="resolved.eventInfoFieldName"
    :site-id="siteId"
    :board-id="boardId ?? undefined"
    :workflow-instance-id="activeWorkflowInstanceId"
    :workflow-step-id="activeWorkflowStepIdForChild"
    :workflow-definition-id="resolved.workflowDefinitionId"
    :workflow-start-node-id="resolved.workflow?.startNodeId"
    :workflow-definition="resolved.workflowDefinition"
    :workflow-node="activeNode"
    :workflow="resolved.workflow"
    :create-silently="resolved.createSilently"
    :technical-place-default="technicalPlaceDefault"
    :cost-center-default="costCenterDefault"
    :user-option-ids="modalUserOptionIds"
    :user-field-option-ids="userFieldOptionIds"
    :user-field-role-labels="userFieldRoleLabels"
    :responsible-role-label="responsibleRoleLabel"
    workflow-step-host
    @save="handleSave"
    @workflow-next="handleWorkflowNext"
    @update:visible="handleVisible"
    @update:visible-action-modal="handleVisible"
  />
  <SQDCWorkflowParallelStartDialog
    v-if="parallelPreparation != null"
    v-model:visible="parallelStartVisible"
    :preparation="parallelPreparation"
    :definition="activeWorkflowDefinition!"
    :site-id="siteId"
    :board-id="boardId"
    :transition-comment="parallelTransitionRequirements.transitionComment"
    :selected-datapoint-id="parallelTransitionRequirements.selectedDatapointId"
    @committed="handleParallelCommitted"
  />
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, inject, nextTick, ref, watch } from "vue";
import {
  DatePickerType,
  SQDCEventActionTypeFieldType,
  SQDCEventActionTypeType,
  SQDCEventTypeFieldRelation,
  UserFunctionScope,
  EABLoadBoardEventDocument,
  type EABLoadBoardEventQuery,
  SQDCWorkflowInstancesDocument,
  type SQDCWorkflowInstancesQuery,
  SQDCWorkflowParallelPrepareDocument,
  type SQDCWorkflowParallelCommitMutation,
  type SQDCWorkflowParallelPrepareQuery,
  type IEventOption,
  useSQDCActionTypesQuery,
  useSQDCEABShorthandsQuery,
  useUserFunctionsQuery,
} from "@/graphql";
import { useSQDCWorkflowRuntime } from "../../workflow/useSQDCWorkflowRuntime";
import {
  getActiveSQDCWorkflowSteps,
  resolveSQDCWorkflowParallelJoinTarget,
  type SQDCWorkflowDefinition,
  type SQDCWorkflowInstance,
} from "../../workflow/domain";
import {
  actionRequiresUserInput,
  createDefaultActionForWorkflow,
  createDefaultActionFromShorthand,
  createDefaultEventForWorkflow,
  createDefaultEventFromShorthand,
  eventRequiresUserInput,
  getShorthandUserFieldOptionIds,
  getShorthandUserFieldFunctionOverlayIds,
  getShorthandUserFunctionIds,
  getModalEventTypeForLatestRevision,
} from "./modals/SQDCShorthandDefaultHelper";
import { normalizeIsoDateToGranularity } from "./modals/SQDCDateGranularityHelper";
import type {
  ModalCostCenterDefault,
  ModalActionEditType,
  ModalEventEditType,
  ModalEventType,
  ModalTechnicalPlaceDefault,
} from "./modals/SQDCModelHelper";
import { DefaultDateInjectionKey } from "@/views/event-action/EventActionBoardInjectionKeys";
import { DateTime } from "luxon";
import store from "@/store";
import { useApolloClient } from "@vue/apollo-composable";
import { useTranslation } from "@/language/translation";
import SQDCWorkflowParallelStartDialog from "../workflow/SQDCWorkflowParallelStartDialog.vue";
import { addSQDCEvent, removeSQDCEvent } from "@/graphql/apolloCacheUtils";
import { toSQDCWorkflowInstance } from "../../workflow/repository";

type SQDCWorkflowCreateContext = {
  workflowInstanceId?: string;
  workflowStepId?: string;
  connectionId?: string;
  definitionId?: string;
  startNodeId?: string;
  transitionComment?: string;
  selectedDatapointId?: number;
};
type ResponsibleShorthand = {
  responsible?: string | null;
  overlayResponsibleRole?: boolean;
  ResponsibleFunction?: { nameId?: number } | null;
  FieldValues?: Array<unknown>;
};
const { t } = useTranslation();
type SQDCEventActionSavePayload =
  | IEventOption
  | { sqdcEventId: number }
  | { actionId?: number; actionType?: number };

type SQDCEventActionModalSource =
  | {
      kind: "workflow-step";
      workflowInstanceId: string;
      workflowStepId: string;
      connectionId?: string;
      node?: SQDCWorkflowDefinition["nodes"][number];
      eventId?: number;
      actionId?: number;
      eventOwnerId?: number;
      lastWorkflowUserId?: number;
      transitionComment?: string;
      selectedDatapointId?: number;
      workflowDefinition?: SQDCWorkflowDefinition;
    }
  | {
      kind: "workflow-transition";
      workflowInstanceId: string;
      workflowStepId: string;
      connectionId: string;
      node: SQDCWorkflowDefinition["nodes"][number];
      eventId?: number;
      actionId?: number;
      eventOwnerId?: number;
      transitionComment?: string;
      selectedDatapointId?: number;
      workflowDefinition?: SQDCWorkflowDefinition;
    }
  | {
      kind: "event-edit";
      eventId: number;
      eventType: ModalEventType;
      initialConversionTargetId?: number;
      workflowInstanceId?: string;
      workflowStepId?: string;
    }
  | {
      kind: "action-edit";
      actionId: number;
      eventId?: number;
      actionType: ModalEventType;
      eventFieldId?: number;
      eventInfoFieldName?: string;
      workflowInstanceId?: string;
      workflowStepId?: string;
    }
  | {
      kind: "event-create";
      eventType: ModalEventType;
      defaultEvent?: ModalEventEditType;
      userOptionIds?: number[];
      workflow?: SQDCWorkflowCreateContext;
      workflowDefinition?: SQDCWorkflowDefinition;
      workflowNode?: SQDCWorkflowDefinition["nodes"][number];
      responsibleShorthand?: ResponsibleShorthand;
    }
  | {
      kind: "action-create";
      actionType: ModalEventType;
      eventId: number;
      eventOwnerId?: number;
      eventFieldId?: number;
      eventInfoFieldName?: string;
      defaultAction?: ModalActionEditType;
      userOptionIds?: number[];
      workflow?: SQDCWorkflowCreateContext;
      workflowDefinition?: SQDCWorkflowDefinition;
      workflowNode?: SQDCWorkflowDefinition["nodes"][number];
      responsibleShorthand?: ResponsibleShorthand;
    };

const props = defineProps<{
  source: SQDCEventActionModalSource;
  siteId: number;
  boardId?: number | null;
  technicalPlaceDefault?: ModalTechnicalPlaceDefault;
  costCenterDefault?: ModalCostCenterDefault;
}>();

const emit = defineEmits<{
  close: [];
  removedFromBoard: [eventId: number];
  save: [SQDCEventActionSavePayload];
  workflowNext: [
    {
      workflowStepId?: string;
      eventId?: number;
      workflowInstanceId?: string;
      transitionComment?: string;
      selectedDatapointId?: number;
    },
  ];
}>();

const modalVisible = ref(true);
const parallelStartVisible = ref(false);
const parallelPreparation = ref<SQDCWorkflowParallelPrepareQuery["SQDCWorkflowParallelPrepare"]>();
const parallelCommitted = ref(false);
const parallelTransitionRequirements = ref<{
  transitionComment?: string;
  selectedDatapointId?: number;
}>({});
const currentSource = ref<SQDCEventActionModalSource>(props.source);
const activeWorkflowStepId = ref(
  currentSource.value.kind === "workflow-step" || currentSource.value.kind === "workflow-transition"
    ? currentSource.value.workflowStepId
    : undefined,
);
const activeEventId = ref(
  currentSource.value.kind === "workflow-step" || currentSource.value.kind === "workflow-transition"
    ? currentSource.value.eventId
    : undefined,
);
const isAdvancing = ref(false);
const defaultDate = inject(
  DefaultDateInjectionKey,
  computed(() => ({ isoString: DateTime.now().toISO(), editable: true })),
);
const defaultUserId = computed(() =>
  store.getters["login/isAnonym"] ? undefined : store.getters["login/userId"],
);
const { client: apolloClient } = useApolloClient();

const workflowSource = computed(() =>
  currentSource.value.kind === "workflow-step" || currentSource.value.kind === "workflow-transition"
    ? currentSource.value
    : undefined,
);
const workflowTransitionSource = computed(() =>
  currentSource.value.kind === "workflow-transition" ? currentSource.value : undefined,
);
const editWorkflowSource = computed(() =>
  currentSource.value.kind === "event-edit" || currentSource.value.kind === "action-edit"
    ? currentSource.value
    : undefined,
);
const createWorkflowSource = computed(() =>
  currentSource.value.kind === "event-create" || currentSource.value.kind === "action-create"
    ? currentSource.value
    : undefined,
);
const {
  instance: runtimeWorkflowInstance,
  step,
  definition: runtimeWorkflowDefinition,
  refetchInstances,
} = useSQDCWorkflowRuntime(
  () => workflowSource.value?.workflowInstanceId ?? editWorkflowSource.value?.workflowInstanceId,
  () => activeWorkflowStepId.value ?? editWorkflowSource.value?.workflowStepId,
);
const activeNode = computed(
  () => workflowSource.value?.node ?? createWorkflowSource.value?.workflowNode ?? step.value?.node,
);
const activeWorkflowDefinition = computed(() => {
  if (currentSource.value.kind === "event-create" || currentSource.value.kind === "action-create") {
    return currentSource.value.workflowDefinition;
  }
  if (currentSource.value.kind === "workflow-step") {
    return currentSource.value.workflowDefinition ?? runtimeWorkflowDefinition.value;
  }
  return runtimeWorkflowDefinition.value;
});

const { result: typeResult } = useSQDCActionTypesQuery(
  computed(() => {
    const isAction = activeNode.value?.type === "ACTION";
    return {
      boardIds: props.boardId == null ? undefined : [props.boardId],
      siteIds: props.boardId == null ? [props.siteId] : undefined,
      types: isAction ? SQDCEventActionTypeType.ACTION : SQDCEventActionTypeType.EVENT,
    };
  }),
  { fetchPolicy: "cache-first" },
);
const { result: shorthandResult, loading: loadingShorthand } = useSQDCEABShorthandsQuery(
  computed(() => ({
    ids: activeNode.value?.shorthandId == null ? undefined : [activeNode.value.shorthandId],
  })),
  { fetchPolicy: "cache-first" },
);
const shorthand = computed(() =>
  shorthandResult.value?.SQDCEABShorthands.find(
    (candidate) => candidate.id === activeNode.value?.shorthandId,
  ),
);
const shorthandUserOptionIds = computed(() =>
  shouldApplyUserOptions(workflowType.value)
    ? (shorthand.value?.UserOptions.map((option) => option.User?.id).filter(
        (id): id is number => id != null,
      ) ?? [])
    : [],
);
const sourceUserOptionIds = computed(() =>
  (currentSource.value.kind === "event-create" &&
    shouldApplyUserOptions(currentSource.value.eventType)) ||
  (currentSource.value.kind === "action-create" &&
    shouldApplyUserOptions(currentSource.value.actionType))
    ? (currentSource.value.userOptionIds ?? [])
    : [],
);
const modalUserOptionIds = computed(() =>
  shorthandUserOptionIds.value.length > 0
    ? shorthandUserOptionIds.value
    : sourceUserOptionIds.value,
);
const responsibleShorthand = computed(
  () => shorthand.value ?? createWorkflowSource.value?.responsibleShorthand,
);
const customUserFunctionIds = computed(() =>
  getShorthandUserFunctionIds(responsibleShorthand.value as never),
);
const { result: userFunctionResult, loading: userFunctionsLoading } = useUserFunctionsQuery(
  () => ({ scopes: [UserFunctionScope.SITE], siteIds: [props.siteId] }),
  () => ({ enabled: customUserFunctionIds.value.length > 0, fetchPolicy: "cache-first" }),
);
const customUserFunctionsReady = computed(
  () => customUserFunctionIds.value.length === 0 || !userFunctionsLoading.value,
);
const userFunctionAssignments = computed(() =>
  Object.fromEntries(
    (userFunctionResult.value?.UserFunctions ?? [])
      .filter((userFunction) => customUserFunctionIds.value.includes(userFunction.id))
      .map((userFunction) => [userFunction.id, userFunction.Assignments]),
  ),
);
const userFieldOptionIds = computed(() =>
  getShorthandUserFieldOptionIds(responsibleShorthand.value as never),
);
const userFieldRoleLabels = computed(() => {
  const functionIds = getShorthandUserFieldFunctionOverlayIds(responsibleShorthand.value as never);
  return Object.fromEntries(
    Object.entries(functionIds).flatMap(([fieldId, functionId]) => {
      const userFunction = userFunctionResult.value?.UserFunctions.find(
        (candidate) => candidate.id === functionId,
      );
      return userFunction == null
        ? []
        : [[Number(fieldId), String(t(`dynamic.${userFunction.nameId}`))] as const];
    }),
  );
});
const responsibleRoleLabel = computed(() => {
  if (responsibleShorthand.value?.overlayResponsibleRole !== true) {
    return undefined;
  }
  if (responsibleShorthand.value.responsible === "USER_FUNCTION") {
    return responsibleShorthand.value.ResponsibleFunction?.nameId == null
      ? undefined
      : String(t(`dynamic.${responsibleShorthand.value.ResponsibleFunction.nameId}`));
  }
  if (responsibleShorthand.value.responsible === "LAST_WORKFLOW_USER") {
    return String(t("modules.sqdc.components.EABWokflow.EABShorthandForm.lastWorkflowUser"));
  }
  return undefined;
});
const workflowStepReady = computed(
  () =>
    (workflowSource.value?.workflowInstanceId == null ||
      runtimeWorkflowInstance.value?.workflowInstanceId ===
        workflowSource.value.workflowInstanceId) &&
    (activeNode.value?.shorthandId == null ||
      (!loadingShorthand.value && shorthand.value?.id === activeNode.value.shorthandId)),
);
const workflowType = computed(() => {
  const type =
    shorthand.value?.EventActionType ??
    typeResult.value?.SQDCEventActionTypes.find(
      (candidate) => candidate.id === activeNode.value?.eventActionTypeId,
    );
  return type == null
    ? undefined
    : getModalEventTypeForLatestRevision(type as unknown as ModalEventType);
});
const workflowContext = computed<SQDCWorkflowCreateContext | undefined>(() =>
  workflowSource.value?.connectionId == null ||
  activeWorkflowStepId.value == null ||
  workflowSource.value?.workflowInstanceId == null
    ? undefined
    : {
        workflowInstanceId: workflowSource.value.workflowInstanceId,
        workflowStepId: activeWorkflowStepId.value,
        connectionId: workflowSource.value.connectionId,
        transitionComment: workflowSource.value.transitionComment,
        selectedDatapointId: workflowSource.value.selectedDatapointId,
      },
);
const lastWorkflowUserId = computed(
  () =>
    (workflowSource.value?.kind === "workflow-step"
      ? workflowSource.value.lastWorkflowUserId
      : undefined) ??
    (step.value != null && "responsibleUserId" in step.value
      ? step.value.responsibleUserId
      : undefined),
);

function withWorkflowVariableEventDefaults(defaultEvent: ModalEventEditType) {
  const result = structuredClone(defaultEvent);
  for (const binding of activeNode.value?.variableBindings ?? []) {
    if (!binding.readonly && !binding.read) continue;
    const value = runtimeWorkflowInstance.value?.variableValues?.[binding.variableId];
    result.SQDCEventTextFields = result.SQDCEventTextFields.filter(
      (field) => field.fieldId !== binding.fieldId,
    );
    if (value != null) result.SQDCEventTextFields.push({ fieldId: binding.fieldId, value });
  }
  return result;
}

function withWorkflowVariableActionDefaults(defaultAction: ModalActionEditType) {
  const result = structuredClone(defaultAction);
  for (const binding of activeNode.value?.variableBindings ?? []) {
    if (!binding.readonly && !binding.read) continue;
    const value = runtimeWorkflowInstance.value?.variableValues?.[binding.variableId];
    result.SQDCActionTextFields = result.SQDCActionTextFields.filter(
      (field) => field.fieldId !== binding.fieldId,
    );
    if (value != null) result.SQDCActionTextFields.push({ fieldId: binding.fieldId, value });
  }
  return result;
}

const resolved = computed(() => {
  if (currentSource.value.kind === "event-edit") {
    return {
      kind: "event" as const,
      eventId: currentSource.value.eventId,
      eventType: currentSource.value.eventType,
      initialConversionTargetId: currentSource.value.initialConversionTargetId,
    };
  }
  if (currentSource.value.kind === "action-edit") {
    return {
      kind: "action" as const,
      actionId: currentSource.value.actionId,
      eventId: currentSource.value.eventId,
      actionType: currentSource.value.actionType,
      eventFieldId: currentSource.value.eventFieldId,
      eventInfoFieldName: currentSource.value.eventInfoFieldName,
    };
  }
  if (currentSource.value.kind === "event-create") {
    const eventType = currentSource.value.eventType;
    const shorthandDefault = currentSource.value.responsibleShorthand
      ? createDefaultEventFromShorthand(
          currentSource.value.responsibleShorthand as never,
          props.siteId,
          userFunctionAssignments.value,
        )
      : currentSource.value.defaultEvent;
    const defaultEvent = withEventFallbacks(
      shorthandDefault == null || currentSource.value.defaultEvent == null
        ? shorthandDefault
        : {
            ...shorthandDefault,
            eisenhowerPriority: currentSource.value.defaultEvent.eisenhowerPriority,
            flagIds: [...currentSource.value.defaultEvent.flagIds],
          },
      eventType,
    );
    return {
      kind: "event" as const,
      eventType,
      defaultEvent,
      workflow: currentSource.value.workflow,
      workflowDefinitionId: currentSource.value.workflow?.definitionId,
      workflowDefinition: currentSource.value.workflowDefinition,
      createSilently: defaultEvent != null && !eventRequiresUserInput(eventType, defaultEvent),
    };
  }
  if (currentSource.value.kind === "action-create") {
    const actionType = getModalEventTypeForLatestRevision(currentSource.value.actionType);
    const shorthandDefault = currentSource.value.responsibleShorthand
      ? createDefaultActionFromShorthand(
          currentSource.value.responsibleShorthand as never,
          currentSource.value.eventOwnerId,
          props.siteId,
          userFunctionAssignments.value,
        )
      : currentSource.value.defaultAction;
    const defaultAction = withActionFallbacks(shorthandDefault, actionType);
    return {
      kind: "action" as const,
      actionType,
      eventId: currentSource.value.eventId,
      eventFieldId: currentSource.value.eventFieldId,
      eventInfoFieldName: currentSource.value.eventInfoFieldName,
      defaultAction,
      workflow: currentSource.value.workflow,
      workflowDefinitionId: currentSource.value.workflow?.definitionId,
      workflowDefinition: currentSource.value.workflowDefinition,
      createSilently: defaultAction != null && !actionRequiresUserInput(actionType, defaultAction),
    };
  }
  if (currentSource.value.kind === "workflow-transition") {
    return { kind: "loading" as const };
  }
  if (!workflowStepReady.value || activeNode.value == null) {
    return { kind: "loading" as const };
  }
  if (activeNode.value.type === "EVENT") {
    const eventType = workflowType.value;
    if (workflowSource.value?.connectionId == null && workflowSource.value?.eventId != null) {
      return {
        kind: "event" as const,
        eventId: workflowSource.value.eventId,
        eventType,
        workflowDefinitionId: activeWorkflowDefinition.value?.id,
        workflowDefinition: activeWorkflowDefinition.value,
      };
    }
    const workflowDefaultEvent = withWorkflowVariableEventDefaults(
      createDefaultEventForWorkflow(
        shorthand.value,
        props.siteId,
        lastWorkflowUserId.value,
        userFunctionAssignments.value,
      ),
    );
    const defaultEvent =
      eventType == null
        ? workflowDefaultEvent
        : (withEventFallbacks(workflowDefaultEvent, eventType) ?? workflowDefaultEvent);
    return {
      kind: "event" as const,
      eventType,
      defaultEvent,
      workflow: workflowContext.value,
      workflowDefinitionId: activeWorkflowDefinition.value?.id,
      workflowDefinition: activeWorkflowDefinition.value,
      createSilently:
        workflowContext.value != null &&
        eventType != null &&
        !eventRequiresUserInput(eventType, defaultEvent),
    };
  }
  const actionType = workflowType.value;
  if (
    workflowSource.value?.connectionId == null &&
    workflowSource.value?.actionId != null &&
    activeEventId.value != null
  ) {
    return {
      kind: "action" as const,
      actionId: workflowSource.value.actionId,
      eventId: activeEventId.value,
      actionType,
      workflowDefinitionId: activeWorkflowDefinition.value?.id,
      workflowDefinition: activeWorkflowDefinition.value,
    };
  }
  const defaultAction = withWorkflowVariableActionDefaults(
    createDefaultActionForWorkflow(
      workflowSource.value?.eventOwnerId,
      shorthand.value,
      props.siteId,
      lastWorkflowUserId.value,
      userFunctionAssignments.value,
    ),
  );
  const effectiveDefaultAction =
    actionType == null
      ? defaultAction
      : (withActionFallbacks(defaultAction, actionType) ?? defaultAction);
  return {
    kind: "action" as const,
    actionType,
    eventId: activeEventId.value,
    defaultAction: effectiveDefaultAction,
    workflow: workflowContext.value,
    workflowDefinitionId: activeWorkflowDefinition.value?.id,
    workflowDefinition: activeWorkflowDefinition.value,
    createSilently:
      workflowContext.value != null &&
      actionType != null &&
      !actionRequiresUserInput(actionType, effectiveDefaultAction),
  };
});

const modalKey = computed(() =>
  currentSource.value.kind === "workflow-step" || currentSource.value.kind === "workflow-transition"
    ? `${activeWorkflowStepId.value}-${currentSource.value.connectionId ?? "active"}`
    : currentSource.value.kind,
);
const activeWorkflowInstanceId = computed(
  () => workflowSource.value?.workflowInstanceId ?? editWorkflowSource.value?.workflowInstanceId,
);
const activeWorkflowStepIdForChild = computed(
  () => activeWorkflowStepId.value ?? editWorkflowSource.value?.workflowStepId,
);

const SQDCModalEvent = defineAsyncComponent(() => import("./modals/SQDCModalEvent.vue"));
const SQDCModalAction = defineAsyncComponent(() => import("./modals/SQDCModalAction.vue"));

function withEventFallbacks(
  defaultEvent: ModalEventEditType | undefined,
  eventType?: ModalEventType,
): ModalEventEditType | undefined {
  if (defaultEvent == null) return undefined;
  const event = {
    ...defaultEvent,
    SQDCEventCostCenterFields: [...defaultEvent.SQDCEventCostCenterFields],
    SQDCEventTechnicalPlaceFields: [...defaultEvent.SQDCEventTechnicalPlaceFields],
    creatorId: defaultEvent.creatorId ?? defaultUserId.value,
    occurrenceDate: normalizeIsoDateToGranularity(
      defaultEvent.occurrenceDate ?? defaultDate.value.isoString,
      DatePickerType.MINUTE,
    ),
  };
  applyEventRelationDefaults(event, eventType);
  return event;
}

function withActionFallbacks(
  defaultAction: ModalActionEditType | undefined,
  actionType?: ModalEventType,
): ModalActionEditType | undefined {
  if (defaultAction == null) return undefined;
  const action = {
    ...defaultAction,
    SQDCActionCostCenterFields: [...defaultAction.SQDCActionCostCenterFields],
    SQDCActionTechnicalPlaceFields: [...defaultAction.SQDCActionTechnicalPlaceFields],
    assigneeId: defaultAction.assigneeId,
    deadline: defaultAction.deadline,
  };
  applyActionRelationDefaults(action, actionType);
  return action;
}

function applyEventRelationDefaults(event: ModalEventEditType, eventType?: ModalEventType) {
  eventType?.SQDCEventActionTypeFields.forEach((field) => {
    if (
      field.type !== SQDCEventActionTypeFieldType.RELATION ||
      !field.preselectBoardRelation ||
      !field.relationHasCategory
    ) {
      return;
    }
    const defaultId = getRelationDefaultId(field.relation);
    if (defaultId == null) return;
    if (
      field.relation === SQDCEventTypeFieldRelation.COST_CENTER &&
      !event.SQDCEventCostCenterFields.some((row) => row.fieldId === field.id)
    ) {
      event.SQDCEventCostCenterFields.push({
        fieldId: field.id,
        index: 0,
        value: defaultId,
      });
    }
    if (
      field.relation === SQDCEventTypeFieldRelation.TECHNICAL_PLACE &&
      !event.SQDCEventTechnicalPlaceFields.some((row) => row.fieldId === field.id)
    ) {
      event.SQDCEventTechnicalPlaceFields.push({
        fieldId: field.id,
        index: 0,
        value: defaultId,
      });
    }
  });
}

function applyActionRelationDefaults(action: ModalActionEditType, actionType?: ModalEventType) {
  actionType?.SQDCEventActionTypeFields.forEach((field) => {
    if (
      field.type !== SQDCEventActionTypeFieldType.RELATION ||
      !field.preselectBoardRelation ||
      !field.relationHasCategory
    ) {
      return;
    }
    const defaultId = getRelationDefaultId(field.relation);
    if (defaultId == null) return;
    if (
      field.relation === SQDCEventTypeFieldRelation.COST_CENTER &&
      !action.SQDCActionCostCenterFields.some((row) => row.fieldId === field.id)
    ) {
      action.SQDCActionCostCenterFields.push({
        fieldId: field.id,
        index: 0,
        value: defaultId,
      });
    }
    if (
      field.relation === SQDCEventTypeFieldRelation.TECHNICAL_PLACE &&
      !action.SQDCActionTechnicalPlaceFields.some((row) => row.fieldId === field.id)
    ) {
      action.SQDCActionTechnicalPlaceFields.push({
        fieldId: field.id,
        index: 0,
        value: defaultId,
      });
    }
  });
}

function getRelationDefaultId(relation?: SQDCEventTypeFieldRelation | null) {
  if (relation === SQDCEventTypeFieldRelation.COST_CENTER) return props.costCenterDefault?.id;
  if (relation === SQDCEventTypeFieldRelation.TECHNICAL_PLACE) {
    return props.technicalPlaceDefault?.id;
  }
  return undefined;
}

function shouldApplyUserOptions(eventActionType: ModalEventType | undefined) {
  return (
    eventActionType?.type === SQDCEventActionTypeType.ACTION ||
    eventActionType?.SQDCEventActionTypeFields.some(
      (field) =>
        field.type === SQDCEventActionTypeFieldType.RELATION &&
        field.relation === SQDCEventTypeFieldRelation.USER,
    ) === true
  );
}

function handleVisible(visible: boolean) {
  if (visible) return;
  if (isAdvancing.value) {
    modalVisible.value = false;
    return;
  }
  emit("close");
}

function handleRemovedFromBoard(eventId: number) {
  if (props.boardId != null) {
    removeSQDCEvent(eventId, props.boardId);
  }
  modalVisible.value = false;
  emit("removedFromBoard", eventId);
  emit("close");
  void apolloClient.refetchQueries({ include: ["BoardEventActionCounts"] });
}

async function handleSave(payload: SQDCEventActionSavePayload) {
  emit("save", payload);
  const pendingWorkflowSource = workflowSource.value;
  if (pendingWorkflowSource?.connectionId == null) {
    await presentInitialAutomaticInput(payload);
    return;
  }
  isAdvancing.value = true;
  modalVisible.value = false;
  const workflowInstance = await loadWorkflowInstance(pendingWorkflowSource.workflowInstanceId);
  if (
    workflowInstance != null &&
    (await presentPendingAutomaticInputs(workflowInstance, pendingWorkflowSource.eventOwnerId))
  ) {
    return;
  }
  emit("close");
}

async function loadWorkflowInstance(workflowInstanceId: string) {
  const result = await apolloClient.query<SQDCWorkflowInstancesQuery>({
    query: SQDCWorkflowInstancesDocument,
    variables: { ids: [workflowInstanceId] },
    fetchPolicy: "network-only",
  });
  const loadedInstance = result.data.SQDCWorkflowInstances[0];
  return loadedInstance == null ? undefined : toSQDCWorkflowInstance(loadedInstance);
}

async function presentInitialAutomaticInput(payload: SQDCEventActionSavePayload) {
  const source = createWorkflowSource.value;
  const definitionId = source?.workflow?.definitionId;
  if (source == null || definitionId == null) return false;

  const eventId =
    source.kind === "event-create"
      ? "eventId" in payload
        ? payload.eventId
        : "sqdcEventId" in payload
          ? payload.sqdcEventId
          : undefined
      : source.eventId;
  const actionId =
    source.kind === "action-create" && "actionId" in payload ? payload.actionId : undefined;
  if (
    (source.kind === "event-create" && eventId == null) ||
    (source.kind === "action-create" && actionId == null)
  ) {
    return false;
  }

  const result = await apolloClient.query<SQDCWorkflowInstancesQuery>({
    query: SQDCWorkflowInstancesDocument,
    variables: source.kind === "event-create" ? { eventId } : { actionId },
    fetchPolicy: "network-only",
  });
  // Manual Proceed emits `workflow-next` while this lookup is in flight. Do not let the
  // normal save handler overwrite the target step selected by that newer event.
  if (currentSource.value !== source) return false;
  const startedInstance = result.data.SQDCWorkflowInstances.map(toSQDCWorkflowInstance).find(
    (candidate) => candidate.definitionId === definitionId,
  );
  const startedStep =
    startedInstance == null
      ? undefined
      : getActiveSQDCWorkflowSteps(startedInstance).find((stepIteratee) =>
          source.kind === "event-create"
            ? stepIteratee.eventId === eventId
            : stepIteratee.actionId === actionId,
        );
  if (startedInstance == null) return false;

  if (startedStep == null) {
    isAdvancing.value = true;
    modalVisible.value = false;
    if (await presentPendingAutomaticInputs(startedInstance)) return true;
    emit("close");
    return true;
  }

  const activeStepSource: Extract<SQDCEventActionModalSource, { kind: "workflow-step" }> = {
    kind: "workflow-step",
    workflowInstanceId: startedInstance.workflowInstanceId,
    workflowStepId: startedStep.workflowStepId,
    node: startedStep.node,
    eventId,
    actionId,
    workflowDefinition: source.workflowDefinition,
  };
  activeWorkflowStepId.value = startedStep.workflowStepId;
  activeEventId.value = eventId;
  await nextTick();
  if (currentSource.value !== source) return false;
  if (await presentPendingAutomaticStep(startedStep, startedInstance)) return true;

  // With no pending automatic input, preserve the normal first-step edit behavior for a workflow
  // that did not advance beyond its newly created start item.
  currentSource.value = activeStepSource;
  return false;
}

async function presentPendingAutomaticInputs(
  workflowInstance: SQDCWorkflowInstance,
  eventOwnerId?: number,
) {
  const definition = activeWorkflowDefinition.value;
  if (definition == null) return false;
  const pendingSteps = getActiveSQDCWorkflowSteps(workflowInstance).filter((candidate) =>
    definition.connections.some(
      (connection) => connection.sourceNodeId === candidate.node.id && connection.autoProceed,
    ),
  );
  if (pendingSteps.length !== 1) return false;
  return presentPendingAutomaticStep(pendingSteps[0], workflowInstance, eventOwnerId);
}

async function presentPendingAutomaticStep(
  createdStep: {
    workflowStepId: string;
    node: SQDCWorkflowDefinition["nodes"][number];
    parallelBranchId?: string;
    eventId?: number;
    actionId?: number;
    responsibleUserId?: number;
  },
  workflowInstance: SQDCWorkflowInstance,
  eventOwnerId?: number,
): Promise<boolean> {
  const definition = activeWorkflowDefinition.value;
  if (definition == null) return false;
  const automaticConnection = definition.connections.find(
    (connection) => connection.sourceNodeId === createdStep.node.id && connection.autoProceed,
  );
  if (automaticConnection == null) return false;
  let targetNode = definition.nodes.find(({ id }) => id === automaticConnection.targetNodeId);
  if (targetNode == null) return false;
  const eventId = createdStep.eventId ?? activeEventId.value;
  if (automaticConnection.datapointTypeId != null && eventId != null) {
    const eventResult = await apolloClient.query<EABLoadBoardEventQuery>({
      query: EABLoadBoardEventDocument,
      variables: { ids: [eventId] },
      fetchPolicy: "network-only",
    });
    if ((eventResult.data.SQDCEvents[0]?.DatapointEventConnection.length ?? 0) > 1) {
      currentSource.value = {
        kind: "workflow-step",
        workflowInstanceId: workflowInstance.workflowInstanceId,
        workflowStepId: createdStep.workflowStepId,
        node: createdStep.node,
        eventId,
        actionId: createdStep.actionId,
        eventOwnerId,
        lastWorkflowUserId: createdStep.responsibleUserId,
        workflowDefinition: definition,
      };
      activeWorkflowStepId.value = createdStep.workflowStepId;
      activeEventId.value = eventId;
      await reopenWorkflowModal();
      return true;
    }
  }
  const completesParallelBoundary =
    targetNode.type === "PARALLEL" &&
    createdStep.node.parentParallelNodeId === targetNode.id &&
    createdStep.parallelBranchId != null;
  if (completesParallelBoundary) {
    const continuationTarget = resolveSQDCWorkflowParallelJoinTarget(
      workflowInstance,
      definition,
      createdStep.parallelBranchId!,
    );
    if (continuationTarget == null) return false;
    targetNode = continuationTarget;
  }
  if (targetNode.type === "END") {
    return false;
  }
  await handleWorkflowNext({
    workflowStepId: createdStep.workflowStepId,
    workflowInstanceId: workflowInstance.workflowInstanceId,
    connectionId: automaticConnection.id,
    node: targetNode,
    eventId,
    lastWorkflowUserId: createdStep.responsibleUserId,
  });
  return true;
}

async function handleWorkflowNext(payload: {
  workflowStepId?: string;
  eventId?: number;
  workflowInstanceId?: string;
  connectionId?: string;
  node?: SQDCWorkflowDefinition["nodes"][number];
  transitionComment?: string;
  selectedDatapointId?: number;
  reconcileInstance?: boolean;
  lastWorkflowUserId?: number;
}) {
  emit("workflowNext", payload);
  isAdvancing.value = true;
  if (payload.reconcileInstance && payload.workflowInstanceId != null) {
    modalVisible.value = false;
    const workflowInstance = await loadWorkflowInstance(payload.workflowInstanceId);
    if (
      workflowInstance != null &&
      (await presentPendingAutomaticInputs(workflowInstance, workflowSource.value?.eventOwnerId))
    ) {
      return;
    }
    emit("close");
    return;
  }
  if (
    payload.node?.type === "PARALLEL" &&
    payload.connectionId != null &&
    payload.workflowInstanceId != null &&
    payload.workflowStepId != null &&
    activeWorkflowDefinition.value != null
  ) {
    modalVisible.value = false;
    parallelCommitted.value = false;
    parallelTransitionRequirements.value = {
      transitionComment: payload.transitionComment,
      selectedDatapointId: payload.selectedDatapointId,
    };
    try {
      const result = await apolloClient.query({
        query: SQDCWorkflowParallelPrepareDocument,
        variables: {
          input: {
            workflowInstanceId: payload.workflowInstanceId,
            currentStepId: payload.workflowStepId,
            definitionId: activeWorkflowDefinition.value.id,
            sourceConnectionId: payload.connectionId,
            parallelNodeId: payload.node.id,
          },
        },
        fetchPolicy: "network-only",
      });
      parallelPreparation.value = result.data.SQDCWorkflowParallelPrepare;
      parallelStartVisible.value = true;
    } catch {
      if (currentSource.value.kind === "workflow-transition") {
        emit("close");
        return;
      }
      isAdvancing.value = false;
      modalVisible.value = true;
    }
    return;
  }
  if (
    payload.node != null &&
    payload.connectionId != null &&
    payload.workflowInstanceId != null &&
    payload.workflowStepId != null
  ) {
    modalVisible.value = false;
    await refetchInstances();
    currentSource.value = {
      kind: "workflow-step",
      workflowInstanceId: payload.workflowInstanceId,
      workflowStepId: payload.workflowStepId,
      connectionId: payload.connectionId,
      node: payload.node,
      eventId: payload.eventId ?? activeEventId.value,
      actionId: undefined,
      eventOwnerId: workflowSource.value?.eventOwnerId,
      lastWorkflowUserId: payload.lastWorkflowUserId,
      transitionComment: payload.transitionComment,
      selectedDatapointId: payload.selectedDatapointId,
      workflowDefinition: activeWorkflowDefinition.value,
    };
    activeWorkflowStepId.value = payload.workflowStepId;
    activeEventId.value = payload.eventId ?? activeEventId.value;
    await reopenWorkflowModal();
    return;
  }
  if (currentSource.value.kind !== "workflow-step" || payload.workflowStepId == null) {
    emit("close");
    return;
  }
  emit("close");
}

async function handleParallelCommitted(
  result: SQDCWorkflowParallelCommitMutation["SQDCWorkflowParallelCommit"],
) {
  parallelCommitted.value = true;
  const createdEventIds = result.createdEvents.map(({ id }) => id);
  if (createdEventIds.length > 0) {
    const createdEventsResult = await apolloClient.query<EABLoadBoardEventQuery>({
      query: EABLoadBoardEventDocument,
      variables: { ids: createdEventIds },
      fetchPolicy: "network-only",
    });
    createdEventsResult.data.SQDCEvents.forEach((event) => {
      const boardIds = new Set(event.BoardEventConnection.map(({ boardId }) => boardId));
      if (boardIds.size === 0 && props.boardId != null) boardIds.add(props.boardId);
      boardIds.forEach((boardId) => addSQDCEvent(event, boardId));
    });
  }
  await Promise.all([
    refetchInstances(),
    apolloClient.refetchQueries({
      include: ["EABLoadBoardEvent", "SQDCActions"],
    }),
  ]);
  parallelPreparation.value = undefined;
  parallelTransitionRequirements.value = {};
  parallelStartVisible.value = false;
  const workflowInstance = await loadWorkflowInstance(result.workflowInstanceId);
  if (
    workflowInstance != null &&
    (await presentPendingAutomaticInputs(workflowInstance, workflowSource.value?.eventOwnerId))
  ) {
    return;
  }
  emit("close");
}

let presentedTransitionSource: SQDCEventActionModalSource | undefined;
watch(
  [workflowTransitionSource, activeWorkflowDefinition],
  async ([source, definition]) => {
    if (source == null || definition == null || presentedTransitionSource === source) return;
    presentedTransitionSource = source;
    await handleWorkflowNext({
      workflowStepId: source.workflowStepId,
      eventId: source.eventId,
      workflowInstanceId: source.workflowInstanceId,
      connectionId: source.connectionId,
      node: source.node,
      transitionComment: source.transitionComment,
      selectedDatapointId: source.selectedDatapointId,
    });
  },
  { immediate: true },
);

watch(parallelStartVisible, async (isVisible) => {
  if (isVisible || parallelPreparation.value == null) return;
  parallelPreparation.value = undefined;
  parallelTransitionRequirements.value = {};
  if (parallelCommitted.value) return;
  if (currentSource.value.kind === "workflow-transition") {
    emit("close");
    return;
  }
  await reopenWorkflowModal();
});

async function reopenWorkflowModal() {
  await nextTick();
  isAdvancing.value = false;
  modalVisible.value = true;
}
</script>
